import React, { useCallback, useEffect, useState } from 'react';
import { Save, Plus, X, Shield, AlertCircle, CheckCircle2, AlertTriangle, Ban, HelpCircle } from 'lucide-react';
import toast from '../../design-system/toast';
import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import type { LicenseAction, LicensePolicy } from '../../types/sbom';
import { useOrganization } from '../../contexts/OrganizationContext';

type BucketKey = 'allow' | 'review' | 'deny';

type BucketMeta = {
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
};

const BUCKET_META: Record<BucketKey, BucketMeta> = {
  allow: {
    label: 'Allow',
    description: 'Licenses that pass without comment.',
    icon: CheckCircle2,
    badgeBg: 'bg-semantic-success/10',
    badgeText: 'text-semantic-success',
    badgeBorder: 'border-semantic-success/20',
  },
  review: {
    label: 'Review',
    description: 'Licenses that warrant a manual decision (medium-severity finding).',
    icon: AlertTriangle,
    badgeBg: 'bg-semantic-warning/10',
    badgeText: 'text-semantic-warning',
    badgeBorder: 'border-semantic-warning/20',
  },
  deny: {
    label: 'Deny',
    description: 'Licenses you do not allow in this organization (high-severity finding).',
    icon: Ban,
    badgeBg: 'bg-semantic-error/10',
    badgeText: 'text-semantic-error',
    badgeBorder: 'border-semantic-error/20',
  },
};

const Bucket: React.FC<{
  bucket: BucketKey;
  values: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  disabled?: boolean;
}> = ({ bucket, values, onAdd, onRemove, disabled }) => {
  const [input, setInput] = useState('');
  const meta = BUCKET_META[bucket];
  const Icon = meta.icon;

  const handleAdd = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setInput('');
  };

  return (
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-ui-border-primary flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${meta.badgeBg} ${meta.badgeText} ${meta.badgeBorder}`}
        >
          <Icon className="w-3.5 h-3.5" />
          {meta.label}
        </span>
        <span className="text-sm text-ui-text-secondary">{meta.description}</span>
        <span className="ml-auto text-xs text-ui-text-tertiary">{values.length}</span>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex flex-wrap gap-1.5 min-h-[28px]">
          {values.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium font-mono bg-ui-background-tertiary text-ui-text-primary border border-ui-border-secondary"
            >
              {id}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemove(id)}
                  className="text-ui-text-tertiary hover:text-semantic-error transition-colors"
                  aria-label={`Remove ${id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
          {values.length === 0 && (
            <span className="text-sm text-ui-text-tertiary italic">No SPDX IDs.</span>
          )}
        </div>

        {!disabled && (
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder="SPDX ID, e.g. AGPL-3.0"
              className="flex-1 px-3 py-1.5 text-sm font-mono bg-ui-background-tertiary border border-ui-border-secondary rounded-lg text-ui-text-primary placeholder:text-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
            />
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-ui-text-secondary hover:text-ui-text-primary bg-ui-background-tertiary border border-ui-border-secondary rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const LicensePolicyPage: React.FC = () => {
  const api = useAuthenticatedApi();
  const { canWrite } = useOrganization();
  const [policy, setPolicy] = useState<LicensePolicy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<LicensePolicy>(apiEndpoints.licensePolicy.get());
      setPolicy(response);
    } catch (err: any) {
      setError(err?.message || 'Failed to load license policy');
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (patch: Partial<Pick<LicensePolicy, 'allow' | 'review' | 'deny' | 'unknown_action'>>) => {
    if (!policy) return;
    setPolicy({ ...policy, ...patch });
  };

  const dedupedAdd = (bucket: BucketKey, value: string) => {
    if (!policy) return;
    const next = Array.from(new Set([...policy[bucket], value]));
    update({ [bucket]: next } as any);
  };

  const handleRemove = (bucket: BucketKey, value: string) => {
    if (!policy) return;
    update({ [bucket]: policy[bucket].filter((id) => id !== value) } as any);
  };

  const handleSave = async () => {
    if (!policy) return;
    try {
      setIsSaving(true);
      const response = await api.put<LicensePolicy>(apiEndpoints.licensePolicy.update(), {
        allow: policy.allow,
        review: policy.review,
        deny: policy.deny,
        unknown_action: policy.unknown_action,
      });
      setPolicy(response);
      toast.success('License policy saved');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save policy');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size={40} className="mx-auto mb-4" />
          <p className="text-ui-text-secondary">Loading license policy…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ui-background-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="p-4 bg-semantic-error/10 border border-semantic-error/20 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-semantic-error flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-semantic-error mb-1">Failed to load policy</p>
                <p className="text-sm text-semantic-error">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!policy) return null;

  const disabled = !canWrite;
  const unknownMeta = BUCKET_META[policy.unknown_action];

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-start gap-3 sm:gap-4 mb-6">
          <div className="w-12 h-12 bg-ui-background-secondary border border-ui-border-primary rounded-xl flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 text-brand-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-ui-text-primary">License Policy</h1>
            <p className="text-sm text-ui-text-secondary mt-1">
              Drives the license-related findings emitted by SBOM scans. SPDX IDs are bucketed by allowed action;
              components with no detectable license follow the unknown setting.
            </p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <Bucket
            bucket="allow"
            values={policy.allow}
            onAdd={(v) => dedupedAdd('allow', v)}
            onRemove={(v) => handleRemove('allow', v)}
            disabled={disabled}
          />
          <Bucket
            bucket="review"
            values={policy.review}
            onAdd={(v) => dedupedAdd('review', v)}
            onRemove={(v) => handleRemove('review', v)}
            disabled={disabled}
          />
          <Bucket
            bucket="deny"
            values={policy.deny}
            onAdd={(v) => dedupedAdd('deny', v)}
            onRemove={(v) => handleRemove('deny', v)}
            disabled={disabled}
          />
        </div>

        <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-ui-border-primary flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border bg-ui-background-tertiary text-ui-text-secondary border-ui-border-secondary">
              <HelpCircle className="w-3.5 h-3.5" />
              Unknown
            </span>
            <span className="text-sm text-ui-text-secondary">
              Action when a component has no detectable license metadata.
            </span>
            <span
              className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${unknownMeta.badgeBg} ${unknownMeta.badgeText} ${unknownMeta.badgeBorder}`}
            >
              Currently: {unknownMeta.label}
            </span>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(['allow', 'review', 'deny'] as LicenseAction[]).map((action) => {
                const meta = BUCKET_META[action];
                const isSelected = policy.unknown_action === action;
                return (
                  <button
                    key={action}
                    type="button"
                    onClick={() => !disabled && update({ unknown_action: action })}
                    disabled={disabled}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? `${meta.badgeBg} ${meta.badgeBorder}`
                        : 'bg-ui-background-tertiary border-ui-border-secondary hover:border-ui-border-primary'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <meta.icon
                      className={`w-4 h-4 flex-shrink-0 ${isSelected ? meta.badgeText : 'text-ui-text-tertiary'}`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        isSelected ? meta.badgeText : 'text-ui-text-primary'
                      }`}
                    >
                      {meta.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {!disabled && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primaryDark text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Spinner size={16} tone="current" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save policy
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LicensePolicyPage;
